import { memo } from 'react';
import { type CustomizationMachineTypes } from '@sg/shared-fe/features/customization';

import { useDietaryRestrictions } from '@order/components';

import { ProductDetailsIngredientCard } from '../ProductDetailsIngredientCard';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationsGroupCard = memo((props: Props) => {
  const {
    modification,
    activeModificationsMap,
    maxModificationsCount = Number.POSITIVE_INFINITY,
    groupLimits,
    activeStepperId,

    onIncrement,
    onDecrement,
    setActiveStepperId,
    resetStepperTimeout,
  } = props;

  const activeModificationsCount = activeModificationsMap.size;
  const activeModification = activeModificationsMap.get(modification.id);

  const getRestrictionNotice = useDietaryRestrictions({ referrer: 'ProductDetails' }); // prettier-ignore

  if (!modification.ingredient) return null;

  const { ingredient } = modification;
  const { id, dietaryProperties } = ingredient;

  const hasMaxModifications = activeModificationsCount >= maxModificationsCount;
  const activeIngredientQuantity = activeModification?.quantity ?? 0;
  const dietaryRestrictionNote = getRestrictionNotice(dietaryProperties);
  const selectedQuantity = activeModification?.quantity ?? 0;

  const maxAggregateQuantity = groupLimits?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY; // prettier-ignore
  const maxQuantity = maxAggregateQuantity > 0 ? maxAggregateQuantity : Number.POSITIVE_INFINITY; // prettier-ignore
  const isAtMaxQuantity = selectedQuantity >= maxQuantity || hasMaxModifications; // prettier-ignore

  return (
    <ProductDetailsIngredientCard
      key={id}
      ingredientId={id}
      palette="spinach"
      modification={modification}
      dietaryRestrictionNote={dietaryRestrictionNote}
      quantity={activeIngredientQuantity}
      isAtMaxQuantity={isAtMaxQuantity}
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      activeStepperId={activeStepperId}
      setActiveStepperId={setActiveStepperId}
      resetStepperTimeout={resetStepperTimeout}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  modification: CustomizationMachineTypes.ProductModification;
  activeModificationsMap: Map<string, CustomizationMachineTypes.Modification>;
  maxModificationsCount: number | undefined;
  groupLimits: CustomizationMachineTypes.GroupLimits | undefined;

  activeStepperId: string | null;
  onIncrement: (ingredientId: string) => void;
  onDecrement: (ingredientId: string) => void;
  setActiveStepperId: (stepperId: string | null) => void;
  resetStepperTimeout: () => void;
};
